import {
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	HostListener,
	inject,
	ViewEncapsulation,
	output
} from '@angular/core'
import {ISOLATED_BETTING_RULES, provideIsolatedBetslip} from '@/features/betslip/betslip-create/provide-isolated-betslip'
import {BetslipWorkbenchStore, WorkbenchLimitsState} from './betslip-workbench.store'
import {WorkbenchRiskService} from './workbench-risk.service'
import {LiveOddsTicker} from './live-odds-ticker'
import {WorkbenchLimitsPanelComponent} from './workbench-limits-panel.component'
import {WorkbenchOfferPanelComponent} from './workbench-offer-panel.component'
import {WorkbenchBetslipPanelComponent} from './workbench-betslip-panel.component'

/**
 * Dev-tools Betslip workbench — betslip-demo inside the site, running on the production
 * betslip classes. Limits on the left, the demo's fixed offer in the middle, the Betslip on the right.
 *
 * Everything the betslip calculates with is provided again on this component
 * (`provideIsolatedBetslip`), so the workbench has a betslip of its own: the player's betslip and
 * its localStorage entry are never touched. The Liability tab is a mock of the backend's
 * `betslipRiskInfo` reply (`mock-betslip-risk-backend.ts`).
 *
 * Only DevToolsComponent imports this, and production builds replace that with a no-op, so none
 * of it ships.
 */
@Component({
	selector: 'dt-betslip-workbench',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	imports: [WorkbenchLimitsPanelComponent, WorkbenchOfferPanelComponent, WorkbenchBetslipPanelComponent],
	providers: [
		...provideIsolatedBetslip(),
		{
			provide: ISOLATED_BETTING_RULES,
			useFactory: () => {
				const limits = inject(WorkbenchLimitsState)
				return () => limits.bettingRules()
			}
		},
		BetslipWorkbenchStore,
		WorkbenchRiskService,
		LiveOddsTicker
	],
	templateUrl: './betslip-workbench.component.html',
	styleUrl: './betslip-workbench.component.css'
})
export class BetslipWorkbenchComponent {
	readonly closed = output<void>()

	private readonly store = inject(BetslipWorkbenchStore)

	constructor() {
		// Created with the component, so risk info is fetched and live odds move from the start.
		inject(WorkbenchRiskService)
		inject(LiveOddsTicker)

		// Whatever tears the overlay down, the debug betslip does not outlive it.
		inject(DestroyRef).onDestroy(() => this.store.clear())
	}

	@HostListener('document:keydown.escape')
	onEscape(): void {
		this.close()
	}

	/**
	 * Empties the debug betslip before the overlay goes. Its Selections are this workbench's own —
	 * the isolated betslip is a separate instance and never reaches the player's betslip — and
	 * clearing here means the next open starts empty rather than on the last debug session.
	 */
	close(): void {
		this.store.clear()
		this.closed.emit()
	}
}
